import type { UnitId } from "./content/buildings.js";
import type { Micro } from "./exact-numbers.js";
import type { ResearchEffects } from "./research-types.js";

export const POWER_DEPARTMENTS = [
  "industry",
  "defenses",
  "military-production",
  "research",
] as const;
export type PowerDepartment = (typeof POWER_DEPARTMENTS)[number];

export const METAL_LANES = ["construction", "producer-factories", "troop-assembly"] as const;
export type MetalLane = (typeof METAL_LANES)[number];

export const FACTORY_WORKS_OUTPUTS = ["rig-factory", "yard-factory", "robot-factory"] as const;
export type FactoryWorksOutput = (typeof FACTORY_WORKS_OUTPUTS)[number];

export const ECONOMY_BUILDINGS = [
  "mining-rig",
  "power-generator",
  "construction-yard",
  "rig-factory",
  "yard-factory",
  "factory-works",
  "robot-factory",
  "research-lab",
  "shield-generator",
  "supply-depot",
] as const;
export type EconomyBuilding = (typeof ECONOMY_BUILDINGS)[number];

export type BuildingCapacities = Readonly<Record<EconomyBuilding, Micro>>;
export type WreckageCapacities = Readonly<Record<EconomyBuilding, Micro>>;
export type UnitReserves = Readonly<Record<UnitId, Micro>>;
export type PowerSliders = Readonly<Record<PowerDepartment, number>>;
export type FactoryWorksSplit = Readonly<Record<FactoryWorksOutput, number>>;

export interface ManufacturingWorkItem<Target extends string> {
  readonly target: Target;
  readonly remaining: Micro;
}

export interface ManufacturingPlan {
  readonly newConstruction: ManufacturingWorkItem<EconomyBuilding> | null;
  readonly restoration: ManufacturingWorkItem<EconomyBuilding> | null;
  readonly assembly: ManufacturingWorkItem<UnitId> | null;
  readonly constructionSplit: {
    readonly newConstruction: number;
    readonly restoration: number;
  };
  readonly restorationMetalRatio: string;
  readonly restorationWorkRatio: string;
  readonly factoryWorksOperating: boolean;
}

export interface EconomyInput {
  readonly alive: boolean;
  readonly elapsedMs: number;
  readonly metal: Micro;
  readonly researchPoints: Micro;
  readonly units: UnitReserves;
  readonly buildings: BuildingCapacities;
  readonly wreckage: WreckageCapacities;
  readonly powerSliders: PowerSliders;
  readonly powerPriority: readonly PowerDepartment[];
  readonly metalPriority: readonly MetalLane[];
  readonly factoryWorksSplit: FactoryWorksSplit;
  readonly manufacturing: ManufacturingPlan;
  readonly researchEffects: ResearchEffects;
}

export interface ConstraintDiagnostic {
  readonly status: "operating" | "idle" | "power-limited" | "metal-limited";
  readonly requested: Micro;
  readonly received: Micro;
  readonly explanation: string;
}

export interface EconomyDiagnostics {
  readonly availablePower: Micro;
  readonly unusedPower: Micro;
  readonly power: Readonly<Record<PowerDepartment, ConstraintDiagnostic>>;
  readonly metal: Readonly<Record<MetalLane, ConstraintDiagnostic>>;
  readonly grossMetal: Micro;
  readonly metalSpent: Micro;
  readonly netMetal: Micro;
  readonly constructionWork: Micro;
  readonly assemblyWork: Micro;
  readonly researchPoints: Micro;
  readonly shieldRecharge: Micro;
  readonly shieldCapacity: Micro;
  readonly operatedShieldGenerators: Micro;
  readonly supplyCapacity: Micro;
}

export interface EconomySettlement {
  readonly metal: Micro;
  readonly researchPoints: Micro;
  readonly units: UnitReserves;
  readonly buildings: BuildingCapacities;
  readonly wreckage: WreckageCapacities;
  readonly progress: {
    readonly newConstruction: Micro;
    readonly restoration: Micro;
    readonly assembly: Micro;
  };
  readonly diagnostics: EconomyDiagnostics;
}
